/**
 * Top-level filter-bar grouping.
 *
 * `EmbedHost.vue` renders the root tree as a vertical stack. When the
 * author lays out a row of filter primitives at the root — either as
 * a flat sequence of filter siblings OR as an `sbi.columns(...)`
 * whose cells each contain a single filter — and that row is
 * `FILTER_BAR_MIN` deep or more, it collapses into a single stylised
 * "filters bar" container in the host template.
 *
 * The threshold is deliberately "more than 2": one or two filters
 * above a chart read fine on their own; three or more start to look
 * like a stranded toolbar and benefit from a designed surface that
 * groups them visually.
 *
 * Two recognised shapes:
 *
 *   1. Flat: 3+ consecutive root-level filter primitives in a row.
 *      A non-filter sibling between two filters breaks the streak.
 *
 *   2. Columns: a root-level `columns` node where every cell holds
 *      exactly one filter primitive AND the cell count is 3+. The
 *      `columns` grid layout is replaced by the bar's flex-wrap row.
 *
 * A flat run and an adjacent filter-columns merge into one bar (so an
 * author who has 3 flat filters followed by a 2-cell column row of
 * filters gets a single 5-wide bar, not two separate strips).
 *
 * Filters nested deeper (inside a `section`, `accordion`, `tab`,
 * `if`, `drilldown`, or a `columns` cell that also contains other
 * content) are NOT considered — the author chose a container, so we
 * don't second-guess it.
 */

import type { TreeNode } from "../sbi/validate-render-op";

export const FILTER_KINDS: ReadonlySet<TreeNode["kind"]> = new Set([
  "select",
  "multiselect",
  "checkbox_group",
  "toggle",
  "date",
  "date_range",
  "number",
  "number_range",
  "search",
  "relative_date",
]);

export const FILTER_BAR_MIN = 3;

export type RootGroup =
  | { kind: "bar"; nodes: TreeNode[] }
  | { kind: "single"; node: TreeNode };

type ColumnsNode = Extract<TreeNode, { kind: "columns" }>;

/**
 * A `columns` node qualifies as a "filter row in disguise" when every
 * cell holds exactly one filter primitive. The cell-count threshold
 * is enforced separately in `groupTopLevelTree` so a flat-+-columns
 * merge can still cross the threshold even when the columns part
 * alone wouldn't.
 */
function isFilterColumns(node: TreeNode): node is ColumnsNode {
  if (node.kind !== "columns") return false;
  if (node.cells.length === 0) return false;
  return node.cells.every(
    (c) => c.children.length === 1 && FILTER_KINDS.has(c.children[0]!.kind),
  );
}

/**
 * Walk the root-tree array once and bucket adjacent filter-row nodes
 * (flat filter primitives + `columns` rows that contain nothing but
 * single-filter cells) into bar groups. Runs shorter than
 * `FILTER_BAR_MIN` flush back out as individual `single` groups so
 * the existing per-filter / per-columns layout is preserved for 1-
 * and 2-filter cases.
 */
export function groupTopLevelTree(tree: readonly TreeNode[]): RootGroup[] {
  const out: RootGroup[] = [];
  // Buffer holds either filter primitives (from flat siblings) or
  // unwrapped filter children pulled out of a columns row. The
  // original columns wrapper is dropped on the floor when the run
  // becomes a bar; if the run is too short to qualify, we restore
  // the columns wrapper as a single (see `restoredBufferAsSingles`).
  let run: TreeNode[] = [];
  // Mirror of `run` that preserves the original top-level wrappers
  // (so a sub-threshold filter-columns falls back to rendering as a
  // normal columns, not as its unwrapped filter children).
  let runSources: TreeNode[] = [];

  const flushRun = (): void => {
    if (run.length === 0) return;
    if (run.length >= FILTER_BAR_MIN) {
      out.push({ kind: "bar", nodes: run });
    } else {
      // Sub-threshold — emit the original source nodes, not the
      // unwrapped filters, so a 2-cell filter-columns renders as a
      // normal columns rather than as two unboxed filter controls.
      for (const n of runSources) out.push({ kind: "single", node: n });
    }
    run = [];
    runSources = [];
  };

  for (const node of tree) {
    if (FILTER_KINDS.has(node.kind)) {
      run.push(node);
      runSources.push(node);
    } else if (isFilterColumns(node)) {
      for (const cell of node.cells) run.push(cell.children[0]!);
      runSources.push(node);
    } else {
      flushRun();
      out.push({ kind: "single", node });
    }
  }
  flushRun();
  return out;
}
